import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { $getNodeByKey, $getRoot, $getSelection } from 'lexical'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import Bugsnag from '@bugsnag/js'
import { logError } from 'util/debug'
import { doUpdateSearchByKey } from 'actions/search'
import { selectSearchEditorState } from 'selectors/search/searchFilters'
import {
  removeWhitspacesAfterColon,
  getTextFromRoot,
  getCurrentPart,
  CLEAR_SEARCH_TAG,
} from '../util'
import FilterPlugin from './FilterPlugin'
import { FilterNode } from './FilterNode'

const Composer = ({ children, onChange }) => {
  const dispatch = useDispatch()
  const initialEditorState = useSelector(selectSearchEditorState)
  const editorConfig = useMemo(
    () => {
      const config = {
        theme: {
          // Class name for the filter node
          filter: 'data-search-filter',
        },
        nodes: [FilterNode],
        onError: error => {
          logError(error)
          Bugsnag.notify(error)
        },
        // Prevent focusing the editor after set the initial state
        editable: false,
      }
      if (initialEditorState) {
        config.editorState = initialEditorState
      }
      return config
    },
    [initialEditorState]
  )

  const handleChange = useCallback(
    (editorState, _editor, tags) => {
      editorState.read(() => {
        if (tags.has(CLEAR_SEARCH_TAG)) {
          // Pevent onChange from being called when clear the field
          // because we want to handle it differently to also remove the filter badges
          return
        }
        // Read the contents of the EditorState here.
        const root = $getRoot()
        const selection = $getSelection()
        dispatch(doUpdateSearchByKey('editorState', editorState))
        onChange(
          removeWhitspacesAfterColon(getTextFromRoot(root)),
          selection
            ? getCurrentPart($getNodeByKey(selection.anchor.key), root)
            : {}
        )
      })
    },
    [onChange, dispatch]
  )

  return (
    <LexicalComposer initialConfig={editorConfig}>
      {children}
      <OnChangePlugin onChange={handleChange} />
      <HistoryPlugin />
      {/* Support highlighting editable search filters */}
      <FilterPlugin />
    </LexicalComposer>
  )
}

export default Composer
